import { updateQueryStringParameters } from 'common/js/helpers';
import { action, makeAutoObservable, when } from 'mobx';
import joinUrl from 'url-join';
import globalConfig from '../js/config';
import { UITimerStore } from './UITimerStore';
import { generateUrlWithRegion } from '../js/url-utils';
import {
  sendMraidDebugTracks,
  setClickPositionInTrack
} from 'common/js/TrackAnalyticsHelper';
import { DebugLog } from '../js/debug-log';
import { sendSupplyMonitoringEvent as sendSupplyMonitoringEventCore } from 'common/services/supply-monitoring-event.service';
import { sendAdsEventsTrack as sendAdsEventsTrackCore } from 'common/services/ads-events-track.service';
import { DisplayConfigType } from 'formats/display/lib/config';

interface AnalyticsStoreProps {
  uiTimerStore: UITimerStore;
  adContext: AdContext;
  formatConfig: DisplayConfigType;
}

export class AnalyticsStore {
  private static _instance: AnalyticsStore;

  uiTimerStore: UITimerStore;
  adContext: AdContext;
  formatConfig: DisplayConfigType;
  private isImpressionFormatSent: boolean = false;
  private pendingTracks: number = 0;
  private isFormatAvailableInterval: NodeJS.Timeout | undefined;
  private shouldTrackFormatAvailability: boolean = false;

  private trackWaitPromise: Promise<boolean> | null = null;

  static get instance(): AnalyticsStore {
    return this._instance;
  }

  static initStore(analyticsStoreProps: AnalyticsStoreProps): AnalyticsStore {
    if (this.instance) return this.instance;

    return (this._instance = new AnalyticsStore(analyticsStoreProps));
  }

  private constructor({
    uiTimerStore,
    adContext,
    formatConfig
  }: AnalyticsStoreProps) {
    this.uiTimerStore = uiTimerStore;
    this.adContext = adContext;
    this.formatConfig = formatConfig;

    makeAutoObservable(this);
  }

  commonTrackData() {
    return {
      a_sdk: window.MRAID_ENV?.sdkVersion || undefined,
      ak: this.adContext.apiKey,
      auid: this.adContext.adUnitId,
      imp: this.adContext.impressionId,
      oc_id: parseInt(this.adContext.campaignId),
      u_tk: this.adContext.userInstanceToken,
      u_id: this.adContext.userId,
      t_u: this.adContext.isTemporaryUser,
      u_os: this.adContext.deviceOs,
      dmn: this.adContext.domain,
      a_b: this.adContext.appBundle,
      pg: this.adContext.page,
      cr_id: this.adContext.creativeId,
      dsp: this.adContext.dsp,
      a_ex: this.adContext._appExchange
    };
  }

  sendCreativeTrack(eventType: CreativeEvent, event?: ClickEvent) {
    if (window.isOgyPreview) {
      return;
    }
    if (!this.formatConfig || !this.formatConfig.displayTrackers) {
      return;
    }

    const datas: CreativeTrack = {
      ...this.commonTrackData(),
      a_ex: this.adContext._appExchange,
      e: eventType,
      timer: this.uiTimerStore.viewableDuration || undefined
    };

    if (eventType === 'client_click' && event) {
      setClickPositionInTrack(datas, eventType, event);
    }

    DebugLog.log(`sendCreativeTrack: ${eventType}`, datas);

    const url = updateQueryStringParameters(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.FORMAT_ANALYTICS_ENDPOINT,
          this.adContext.region
        ),
        'creative'
      ),
      datas
    );

    this.sendTrack(url);

    return url;
  }

  get impressionFormatSent() {
    return this.isImpressionFormatSent;
  }

  sendImpressionFormat() {
    if (this.shouldTrackFormatAvailability && this.isFormatAvailableInterval) {
      clearInterval(this.isFormatAvailableInterval);
    }

    if (window.isOgyPreview) {
      return;
    }

    /* https://jira.ogury.io/browse/SPY-15465
      The impressionFormatTrack feature-enabling is deactivate on external traffic
      This means, when MRAID_ENV.sdk = 'presage'
      For Equativ, Google Open Bidding, One Tag, this value can vary
      For now we for this impression format to not be sent for Beeswax only (the only case we had)
      before a specific revamp of this in both stack
     */
    if (this.adContext?.dsp === 'beeswax') {
      return;
    }

    const queryParams = {
      ...this.commonTrackData(),
      conn: window?.navigator?.connection?.type
        ? window.navigator.connection.type.toUpperCase()
        : 'WIFI',
      ad_ob: this.adContext.adObject,
      ...this.getRedirectType()
    };

    sendMraidDebugTracks(`format_send_impression_format`);
    DebugLog.log('sendImpressionFormat', queryParams);

    const url = updateQueryStringParameters(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.FORMAT_ANALYTICS_ENDPOINT,
          this.adContext.region
        ),
        'impfmt'
      ),
      queryParams
    );

    this.sendTrack(url);
    this.isImpressionFormatSent = true;

    return url;
  }

  private getRedirectType(): { rt?: string } {
    const rt = this.formatConfig.redirectType;
    return rt ? { rt } : {};
  }

  // loaded and loaded_error tracks are usually sent by the wrapper, but if the format is not Ogury SDK, we need to send it ourselves
  sendAdsEventsTrack(event: 'loaded' | 'loaded_error') {
    this.pendingTracks++;
    return sendAdsEventsTrackCore(
      event,
      this.adContext,
      this.formatConfig
    ).finally(() => this.pendingTracks--);
  }

  sendSupplyMonitoringEvent(
    event: SUPPLY_MONITORING_EVENT_TYPE,
    additionalFields?: OptionalSupplyMonitoringEventFields
  ) {
    this.pendingTracks++;

    const promise = sendSupplyMonitoringEventCore(
      event,
      this.adContext,
      this.formatConfig,
      additionalFields
    );

    return promise.finally(() => this.pendingTracks--);
  }

  waitUntilTracksAreSent(): Promise<boolean> {
    if (this.trackWaitPromise) {
      return this.trackWaitPromise;
    }

    const waitPromise = new Promise<boolean>(resolve => {
      when(() => this.pendingTracks === 0).then(() => resolve(true));
    });

    const timeoutPromise = new Promise<boolean>(resolve =>
      setTimeout(() => resolve(false), 1000)
    );

    this.trackWaitPromise = Promise.race([waitPromise, timeoutPromise]);

    this.trackWaitPromise.finally(() => {
      this.trackWaitPromise = null;
    });

    return this.trackWaitPromise;
  }

  sendTrack(url: string): void {
    this.pendingTracks++;
    const pixel = new Image();
    const callback = action(() => this.pendingTracks--);
    pixel.onload = callback;
    pixel.onerror = callback;
    pixel.src = url;
  }

  sendJsTrack(url: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.srcdoc = `<script src="${url}"></script>`;
    document.body.appendChild(iframe);
  }
}
